import { AbstractControl } from "@angular/forms";
import { ITextValueOption } from '@waseel-asp/tpa-waseel-lib-staging';
import { PreAuthType, PreAuthTypeLabel, PreAuthTypeValue } from "../../../constants/preauth-request.enum";
import { CLAIM_SUB_TYPE, DECISION_STATUS_VALUE } from "../../../constants/system-default-data-constant";
import { BatchStatus, BatchStatusLabel, BatchStatusValue } from "../../../constants/batch-status-enum";
import { ClaimStatus, ClaimStatusLabel, ClaimStatusValue } from "../../../constants/claim-status.enum";
import { ClaimDetailsMap } from "../../claim-card/models/claim-card.model";
import { paymentDetailsMap, PaymentStatus, PaymentStatusLabel, PaymentStatusValue } from "../../../constants/payment-status.enum";
import { Reconciliation, reconciliationDetailsMap, ReconciliationLabel, ReconciliationValue } from "../../../../core/modules/admin-modules/payment-reconciliation/models/payment-reconciliation.model";
import { DenialDetailsLabelMapper, DenialStatus, DenialStatusLabel, DenialStatusValue } from "../../../constants/denial-status";

export interface ICommonChart {
  title: string;
  key: string,
  chartType: ITextValueOption[],
  countType: ITextValueOption[],
  group?: string[],
  otherTitle?: string,
  labels?: string[],
  excludeFileds?: string[],
  isExtraOtherTitleInclude?: boolean;
  commonBarStatus?: ITextValueOption[]
  isOnlyBarChart?: boolean;
  isCountVisible?: boolean;
  status?: { [status: string]: string };
  isAccess?: boolean;
  isReportModule?: boolean;
}

export interface ICommonChartGroup {
  chartType: AbstractControl;
  countType: AbstractControl
}

export enum HighCostApprovalStatus {
  Agree = 1,
  Disagree = 2,
  Pending = 3
}

const HighCostApprovalStatusLabel: { [key in HighCostApprovalStatus]: string } = {
  [HighCostApprovalStatus.Agree]: 'COMMON.AGREE',
  [HighCostApprovalStatus.Disagree]: 'COMMON.DISAGREE',
  [HighCostApprovalStatus.Pending]: 'PATIENT_PORTAL.POLICY.PENDING'
};

export const HighCostApprovalStatusValue: { [key in HighCostApprovalStatus]: string } = {
  [HighCostApprovalStatus.Agree]: DECISION_STATUS_VALUE.AGREE,
  [HighCostApprovalStatus.Disagree]: DECISION_STATUS_VALUE.DISAGREE,
  [HighCostApprovalStatus.Pending]: DECISION_STATUS_VALUE.PENDING,
};

const HighCostApprovalDetailsMapper = {
  [HighCostApprovalStatus.Agree]: {
    title: HighCostApprovalStatusLabel[HighCostApprovalStatus.Agree],
    color: '#4e79a7',
    id: HighCostApprovalStatusValue[HighCostApprovalStatus.Agree]
  },
  [HighCostApprovalStatus.Disagree]: {
    title: HighCostApprovalStatusLabel[HighCostApprovalStatus.Disagree],
    color: '#f28e2b',
    id: HighCostApprovalStatusValue[HighCostApprovalStatus.Disagree]
  },
  [HighCostApprovalStatus.Pending]: {
    title: HighCostApprovalStatusLabel[HighCostApprovalStatus.Pending],
    color: '#e15759',
    id: HighCostApprovalStatusValue[HighCostApprovalStatus.Pending]
  }
};

export enum SLAStatus {
  Breached = 1,
  CompletedWithinSLA = 2,
  NearBreach = 3
}

const SLAStatusLabel: { [key in SLAStatus]: string } = {
  [SLAStatus.Breached]: 'REPORTS.SLA_TRACKING_SUMMARY.STATUS.BREACHED',
  [SLAStatus.CompletedWithinSLA]: 'REPORTS.SLA_TRACKING_SUMMARY.STATUS.COMPLETED_WITHIN_SLA',
  [SLAStatus.NearBreach]: 'REPORTS.SLA_TRACKING_SUMMARY.STATUS.NEAR_BREACH'
};

export const SLAStatusValue: { [key in SLAStatus]: string } = {
  [SLAStatus.Breached]: '44444444-4444-4444-4444-444444444444',
  [SLAStatus.CompletedWithinSLA]: '55555555-5555-5555-5555-555555555555',
  [SLAStatus.NearBreach]: '66666666-6666-6666-6666-666666666666'
};

export const SLADetailsMapper = {
  [SLAStatus.Breached]: {
    title: SLAStatusLabel[SLAStatus.Breached],
    color: '#D7323A',
    backgroundColor: '#dfdee6',
    id: SLAStatusValue[SLAStatus.Breached]
  },
  [SLAStatus.CompletedWithinSLA]: {
    title: SLAStatusLabel[SLAStatus.CompletedWithinSLA],
    color: '#118251',
    backgroundColor: '#f2c4c4',
    id: SLAStatusValue[SLAStatus.CompletedWithinSLA]
  },
  [SLAStatus.NearBreach]: {
    title: SLAStatusLabel[SLAStatus.NearBreach],
    color: '#2936CC',
    backgroundColor: '#d7e9d8',
    id: SLAStatusValue[SLAStatus.NearBreach]
  }
};

const batchStatusDetailsMap = {
  [BatchStatus.Creating]: {
    title: BatchStatusLabel[BatchStatus.Creating],
    color: '#4e79a7',
    backgroundColor: '#e2e3e5',
    id: BatchStatusValue[BatchStatus.Creating]
  },
  [BatchStatus.Posted]: {
    title: BatchStatusLabel[BatchStatus.Posted],
    color: '#f28e2b',
    backgroundColor: '#cfe2ff',
    id: BatchStatusValue[BatchStatus.Posted]
  },
  [BatchStatus.Reviewed]: {
    title: BatchStatusLabel[BatchStatus.Reviewed],
    color: '#e15759',
    backgroundColor: '#d1e7dd',
    id: BatchStatusValue[BatchStatus.Reviewed]
  },
  [BatchStatus.Reviewing]: {
    title: BatchStatusLabel[BatchStatus.Reviewing],
    color: '#76b7b2',
    backgroundColor: '#ffe5d0',
    id: BatchStatusValue[BatchStatus.Reviewing]
  },
  [BatchStatus.Settled]: {
    title: BatchStatusLabel[BatchStatus.Settled],
    color: '#59a14f',
    backgroundColor: '#d1e7dd',
    id: BatchStatusValue[BatchStatus.Settled]
  },
  [BatchStatus.Settling]: {
    title: BatchStatusLabel[BatchStatus.Settling],
    color: '#edc948',
    backgroundColor: '#fff3cd',
    id: BatchStatusValue[BatchStatus.Settling]
  },
  [BatchStatus.Submitted]: {
    title: BatchStatusLabel[BatchStatus.Submitted],
    color: '#af7aa1',
    backgroundColor: '#e0cffc',
    id: BatchStatusValue[BatchStatus.Submitted]
  }
};

const preAuthTypeDetailsMap: Record<PreAuthType, { title: string; color: string; backgroundColor: string; id: string; }> = {
  [PreAuthType.Institutional]: {
    title: PreAuthTypeLabel[PreAuthType.Institutional],
    color: '#4e79a7',
    backgroundColor: '#cfe2ff',
    id: PreAuthTypeValue[PreAuthType.Institutional]
  },
  [PreAuthType.Dental]: {
    title: PreAuthTypeLabel[PreAuthType.Dental],
    color: '#f28e2b',
    backgroundColor: '#d1e7dd',
    id: PreAuthTypeValue[PreAuthType.Dental]
  },
  [PreAuthType.Pharmacy]: {
    title: PreAuthTypeLabel[PreAuthType.Pharmacy],
    color: '#e15759',
    backgroundColor: '#fff3cd',
    id: PreAuthTypeValue[PreAuthType.Pharmacy]
  },
  [PreAuthType.Professional]: {
    title: PreAuthTypeLabel[PreAuthType.Professional],
    color: '#76b7b2',
    backgroundColor: '#e0cffc',
    id: PreAuthTypeValue[PreAuthType.Professional]
  },
  [PreAuthType.Optical]: {
    title: PreAuthTypeLabel[PreAuthType.Optical],
    color: '#59a14f',
    backgroundColor: '#ffe5d0',
    id: PreAuthTypeValue[PreAuthType.Optical]
  }
};


export enum ClaimSubType {
  Emergency = 1,
  InPatient = 2,
  OutPatient = 3
}
//ts-prune-ignore-next
const ClaimSubTypeLabel: { [key in ClaimSubType]: string } = {
  [ClaimSubType.Emergency]: 'ELIGIBILITY.EMERGENCY',
  [ClaimSubType.InPatient]: 'CLAIM.CLAIM_REIMBURSEMENT.REQUEST.INPATIENT',
  [ClaimSubType.OutPatient]: 'CLAIM.CLAIM_REIMBURSEMENT.REQUEST.OUTPATIENT'
};

export const ClaimSubTypeValue: { [key in ClaimSubType]: string } = {
  [ClaimSubType.Emergency]: CLAIM_SUB_TYPE.EMERGENCY,
  [ClaimSubType.InPatient]: CLAIM_SUB_TYPE.IN_PATIENT,
  [ClaimSubType.OutPatient]: CLAIM_SUB_TYPE.OUT_PATIENT
};

const ClaimSubTypeDetailsMapper = {
  [ClaimSubType.Emergency]: {
    title: HighCostApprovalStatusLabel[ClaimSubType.Emergency],
    color: '#4e79a7',
    id: HighCostApprovalStatusValue[ClaimSubType.Emergency]
  },
  [ClaimSubType.InPatient]: {
    title: HighCostApprovalStatusLabel[ClaimSubType.InPatient],
    color: '#f28e2b',
    id: HighCostApprovalStatusValue[ClaimSubType.InPatient]
  },
  [ClaimSubType.OutPatient]: {
    title: HighCostApprovalStatusLabel[ClaimSubType.OutPatient],
    color: '#e15759',
    id: HighCostApprovalStatusValue[ClaimSubType.OutPatient]
  }
};

export const chartConfigMap = {
  claimSummary: {
    enum: ClaimStatus,
    value: ClaimStatusValue,
    label: ClaimStatusLabel,
    colorMap: ClaimDetailsMap,
  },
  preAuthorizationSummary: {
    enum: ClaimStatus,
    value: ClaimStatusValue,
    label: ClaimStatusLabel,
    colorMap: ClaimDetailsMap,
  },
  prescriptionSummary: {
    enum: ClaimStatus,
    value: ClaimStatusValue,
    label: ClaimStatusLabel,
    colorMap: ClaimDetailsMap,
  },
  paymentSummary: {
    enum: PaymentStatus,
    value: PaymentStatusValue,
    label: PaymentStatusLabel,
    colorMap: paymentDetailsMap,
  },
  paymentReconciliationSummary: {
    enum: Reconciliation,
    value: ReconciliationValue,
    label: ReconciliationLabel,
    colorMap: reconciliationDetailsMap,
  },
  batchSummary: {
    enum: BatchStatus,
    value: BatchStatusValue,
    label: BatchStatusLabel,
    colorMap: batchStatusDetailsMap,
  },
  slaTrackingApprovalSummary: {
    enum: SLAStatus,
    value: SLAStatusValue,
    label: SLAStatusLabel,
    colorMap: SLADetailsMapper,
  },
  highCostApprovalSummary: {
    enum: HighCostApprovalStatus,
    value: HighCostApprovalStatusValue,
    label: HighCostApprovalStatusLabel,
    colorMap: HighCostApprovalDetailsMapper,
  },
  processedClaimSummaryByClaimSubType: {
    enum: ClaimSubType,
    value: ClaimSubTypeValue,
    label: ClaimSubTypeLabel,
    colorMap: ClaimSubTypeDetailsMapper,
  },
  processedClaimSummaryByClaimType: {
    enum: PreAuthType,
    value: PreAuthTypeValue,
    label: PreAuthTypeLabel,
    colorMap: preAuthTypeDetailsMap,
  },
  denialSummary: {
    enum: DenialStatus,
    value: DenialStatusValue,
    label: DenialStatusLabel,
    colorMap: DenialDetailsLabelMapper,
  },
};
